// ============================================
// components/gunny/GunnyStepTracker.tsx
// Horizontal 7-step tracker: Society → Supplier → DM → MP → CAO → MD → Paid.
// Step is 'done' when the corresponding timeline timestamp is set;
// 'rejected' when row.status === 'rejected'.
// ============================================
import React from 'react';
import { Check, X } from 'lucide-react';
import type { GunnyProformaRow } from '../../api/services';

export interface GunnyStepTrackerProps {
  row: GunnyProformaRow;
  size?: 'sm' | 'md';
}

interface Step {
  label: string;
  short: string;
  done: boolean;
  tooltip: string;
}

function fmt(ts: string | null): string {
  if (!ts) return '';
  // If already a date-only, keep it. Otherwise format the local portion.
  if (/^\d{4}-\d{2}-\d{2}$/.test(ts)) return ts;
  try {
    const d = new Date(ts);
    if (isNaN(d.valueOf())) return ts;
    return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch {
    return ts;
  }
}

export const GunnyStepTracker: React.FC<GunnyStepTrackerProps> = ({ row, size = 'sm' }) => {
  const t = row.timeline;
  const rejected = row.status === 'rejected';

  const steps: Step[] = [
    { label: 'Society',  short: 'S',  done: !!t.society_done_at,   tooltip: t.society_done_at   ? `Society confirmed ${fmt(t.society_done_at)}`      : 'Waiting on society confirmation' },
    { label: 'Supplier', short: 'Su', done: !!t.supplier_claim_at, tooltip: t.supplier_claim_at ? `Supplier claim filed ${fmt(t.supplier_claim_at)}` : 'Waiting on supplier claim' },
    { label: 'DM',       short: 'D',  done: !!t.dm_verified_at,    tooltip: t.dm_verified_at    ? `DM certified ${fmt(t.dm_verified_at)}`            : 'Waiting on DM certification' },
    { label: 'MP',       short: 'M',  done: !!t.mp_approved_at,    tooltip: t.mp_approved_at    ? `Mgr Proc approved ${fmt(t.mp_approved_at)}`       : 'Waiting on Mgr Proc approval' },
    { label: 'CAO',      short: 'C',  done: !!t.cao_approved_at,   tooltip: t.cao_approved_at   ? `CAO approved ${fmt(t.cao_approved_at)}`           : 'Waiting on CAO approval' },
    { label: 'MD',       short: 'MD', done: !!t.md_approved_at,    tooltip: t.md_approved_at    ? `MD approved ${fmt(t.md_approved_at)}`             : 'Waiting on MD approval' },
    { label: 'Paid',     short: '₹',  done: !!t.last_paid_on,      tooltip: t.last_paid_on      ? `Last paid ${fmt(t.last_paid_on)}`                 : `Balance: ${row.balance_to_release.toLocaleString('en-IN')}` },
  ];

  const dotSize = size === 'sm' ? 'w-5 h-5' : 'w-6 h-6';
  const txtSize = size === 'sm' ? 'text-[10px]' : 'text-xs';
  const connW   = size === 'sm' ? 'w-2' : 'w-3';

  return (
    <div className="inline-flex items-center">
      {steps.map((step, idx) => {
        const connector = idx < steps.length - 1;
        const bgDone   = rejected ? 'bg-red-500'    : 'bg-emerald-600';
        const bgPend   = rejected ? 'bg-red-200'    : 'bg-gray-200';
        const textDone = 'text-white';
        const textPend = rejected ? 'text-red-700' : 'text-gray-500';
        return (
          <React.Fragment key={step.label}>
            <div className="flex flex-col items-center" title={step.tooltip}>
              <div
                className={`${dotSize} rounded-full flex items-center justify-center ${txtSize} font-bold ${step.done ? bgDone + ' ' + textDone : bgPend + ' ' + textPend}`}
              >
                {rejected ? <X className="w-3 h-3" /> : step.done ? <Check className="w-3 h-3" /> : step.short}
              </div>
              <span className={`${txtSize} mt-0.5 ${step.done ? 'text-gray-700' : 'text-gray-400'}`}>{step.label}</span>
            </div>
            {connector && (
              <div className={`${connW} h-0.5 mx-0.5 -mt-4 ${step.done && steps[idx + 1].done ? 'bg-emerald-400' : 'bg-gray-200'}`} />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
};

export default GunnyStepTracker;
